/* eslint-disable react/jsx-no-bind */
import { Box } from '@ouster/webviz-react-components';

import { Tooltip } from '@mui/material';
import { useEffect, useMemo, useState } from 'react';
import {
  MdApartment,
  // MdCallMerge,
  MdKeyboardDoubleArrowLeft,
  MdKeyboardDoubleArrowRight,
  MdOutlineBarChart,
  MdOutlineExitToApp,
  MdOutlineSearch,
  MdOutlineSettings,
  // MdOutlineBadge,
  // MdCloudSync,
  // MdSend,
} from 'react-icons/md';
import { useLocation, useNavigate } from 'react-router-dom';
import { useStoreState } from 'src/reducers';
import styled from 'styled-components';

import useWindowResize from '../hooks/useWindowResize';

const StyledNavContainer = styled.nav`
  display: grid;
`;

const StyledToggle = styled.div<{ collapsed: boolean }>`
  height: 2rem;
  display: none;
  justify-content: center;
  padding: 0;
  align-items: center;
  font-size: 20px;
  cursor: pointer;
  margin: -1rem 0 0;

  border-bottom: 1px solid var(--ouster-colors-gray-700);

  @media (max-width: 1400px) {
    display: flex;
    justify-content: ${(props) => (props.collapsed ? 'center' : 'flex-end')};
    padding-right: ${(props) => (props.collapsed ? 0 : '1rem')};
    background-color: #5e79fb24;
  }
`;

const StyledButton = styled.button<{ collapsed: boolean; variant?: string }>`
  text-align: left;
  display: flex;
  font-size: 14px;
  margin-inline: 1rem;
  column-gap: 0.5rem;
  border-radius: 4px;
  align-items: center;
  padding: 0.5rem 0.75rem;
  font-size: 500;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
  background-color: ${(props) =>
    props.variant === 'solid' ? 'var(--color-blast-off-blue)' : 'transparent'};
  border: 1px solid;
  border-color: ${(props) =>
    props.variant === 'solid' ? 'transparent' : 'var(--color-blast-off-blue)'};
  color: ${(props) =>
    props.variant === 'solid' ? 'white' : 'var(--color-blast-off-blue)'};
  svg {
    font-size: 24px;
  }

  &:hover {
    filter: brightness(130%);
    border-color: var(--ouster-colors-gray-300);
    box-shadow: 0 0 0 2px var(--ouster-colors-gray-700);
  }

  @media (max-width: 1400px) {
    span {
      display: ${(props) => (props.collapsed ? 'none' : 'flex')};
    }
  }
`;

const StyledNavLink = styled.a<{ isActive: boolean; collapsed: boolean }>`
  text-align: left;
  display: flex;
  font-size: 14px;
  column-gap: 0.5rem;
  align-items: center;
  padding: 0.85rem 1.75rem 0.85rem 1.75rem;
  border-bottom: 1px solid var(--ouster-colors-whiteAlpha-200);
  font-weight: 500;
  white-space: nowrap;
  cursor: pointer;
  line-height: 1;
  background-color: ${(props) =>
    props.isActive ? 'var(--ouster-colors-blue-700)' : 'transparent'};
  color: ${(props) =>
    props.isActive ? 'white' : 'var(--ouster-colors-gray-300)'};
  svg {
    font-size: 24px;
  }

  &:hover {
    background-color: ${(props) =>
      props.isActive
        ? 'var(--ouster-colors-blue-700)'
        : 'var(--ouster-colors-gray-800)'};
    color: ${(props) =>
      props.isActive ? 'white' : 'var(--color-blast-off-blue)'};
    cursor: ${(props) => (props.isActive ? 'default' : 'pointer')};
  }

  @media (max-width: 1400px) {
    span {
      display: ${(props) => (props.collapsed ? 'none' : 'flex')};
    }
  }
`;

const StyledSubNavLink = styled(StyledNavLink)`
  padding-left: 2.75rem;
  padding-block: 0.75rem;
  font-weight: 400;
  @media (max-width: 1400px) {
    padding-left: ${(props) => (props.collapsed ? '1.75rem' : '2.75rem')};
  }
`;

const StyledSectionHeader = styled.div<{ collapsed: boolean }>`
  text-align: left;
  display: flex;
  font-size: 11px;
  column-gap: 0.5rem;
  align-items: center;
  padding: 1rem 1.75rem 0.5rem 1.75rem;
  font-weight: 600;
  white-space: nowrap;
  line-height: 1;
  color: var(--ouster-colors-gray-500);
  text-transform: uppercase;
  letter-spacing: 0.05em;
  svg {
    font-size: 18px;
  }

  @media (max-width: 1400px) {
    display: ${(props) => (props.collapsed ? 'none' : 'flex')};
  }
`;

interface Tab {
  name: string;
  submenus?: {
    icon: JSX.Element;
    name: string;
    path: string;
  }[];
}

/**
 * AdminSideNav component for admin-specific navigation
 */
export default function AdminSideNav() {
  const { appUser, curUserOrg } = useStoreState(
    (storeState) => storeState.user
  );

  const navigate = useNavigate();
  const location = useLocation();

  const { width } = useWindowResize();
  const [collapsed, setCollapsed] = useState(width <= 1400);

  // Get cloud team emails from env
  const cloudTeamEmails = useMemo(() => {
    const emails = process.env.REACT_APP_CLOUD_TEAM || '';
    return emails.split(',').map((email) => email.trim());
  }, []);

  // Check if current user is part of cloud team
  const isCloudTeamMember = useMemo(
    () => (appUser?.email ? cloudTeamEmails.includes(appUser.email) : false),
    [appUser?.email, cloudTeamEmails]
  );

  // Check if current user org role is ADMIN
  const isOrgAdmin = useMemo(
    () => curUserOrg?.role === 'ADMIN',
    [curUserOrg?.role]
  );

  const tabs: Tab[] = useMemo(
    () => [
      {
        name: 'Insights',
        submenus: [
          {
            name: 'Growth Trends',
            path: '/admin_dashboard/insights/growth_trends',
            icon: <MdOutlineBarChart />,
          },
        ],
      },
      {
        name: 'User Management',
        submenus: [
          {
            name: 'User Search',
            path: '/admin_dashboard/user_management/user_search',
            icon: <MdOutlineSearch />,
          },
          // hide Role Permissions for now since the page is not ready and we don't want to surface an empty page
          // {
          //   name: 'Role Permissions',
          //   path: '/admin_dashboard/user_management/role_permissions',
          //   icon: <MdOutlineBadge />,
          // },
        ],
      },
      {
        name: 'Organization Hub',
        submenus: [
          // Only show Org Overview if user is org admin
          ...(isOrgAdmin
            ? [
                {
                  name: 'Org Overview',
                  path: '/admin_dashboard/organization_hub/org_overview',
                  icon: <MdApartment />,
                },
              ]
            : []),
          // {
          //   name: 'Provisioning',
          //   path: '/admin_dashboard/organization_hub/provisioning',
          //   icon: <MdCloudSync />,
          // },
          // {
          //   name: 'Merge',
          //   path: '/admin_dashboard/organization_hub/merge',
          //   icon: <MdCallMerge />,
          // },
        ],
      },
      {
        name: 'Recording Controls',
        submenus: [
          {
            name: 'Recording Lookup',
            path: '/admin_dashboard/recording_controls/recording_lookup',
            icon: <MdOutlineSearch />,
          },
          // {
          //   name: 'Distribution',
          //   path: '/admin_dashboard/recording_controls/distribution',
          //   icon: <MdSend />,
          // },
        ],
      },
      // Only show Developer Tools if user is part of cloud team
      ...(isCloudTeamMember
        ? [
            {
              name: 'Developer Tools',
              submenus: [
                {
                  name: 'Recordings Mgmt',
                  path: '/admin_dashboard/developer_tools/recordings_mgmt',
                  icon: <MdOutlineSettings />,
                },
              ],
            },
          ]
        : []),
    ],
    [isOrgAdmin, isCloudTeamMember]
  );

  useEffect(() => {
    setCollapsed(width <= 1400);
  }, [width]);

  function handleToggle() {
    setCollapsed(!collapsed);
  }

  function handleExitAdminMode() {
    // Navigate back to the regular drive/private view
    navigate('/drive/private');
  }

  function handleNavClick(path: string) {
    navigate(path);
  }

  function isPathActive(path: string, submenus?: any[]): boolean {
    const isSubmenuActive =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      submenus?.some((submenu) => location.pathname === submenu.path) ?? false;

    if (collapsed) {
      // If collapsed, consider the menu active when it matches path or has active submenu
      return location.pathname === path || isSubmenuActive;
    }

    // If expanded, consider menu active only when the path match
    return location.pathname === path;
  }

  return (
    <StyledNavContainer data-testid="admin-side-nav-root">
      <StyledToggle
        onClick={handleToggle}
        collapsed={collapsed}
        data-testid="admin-side-nav-toggle"
      >
        {collapsed ? (
          <MdKeyboardDoubleArrowRight />
        ) : (
          <MdKeyboardDoubleArrowLeft />
        )}
      </StyledToggle>

      {/* EXIT ADMIN MODE BUTTON */}
      <Box
        display="grid"
        rowGap="1rem"
        mt="1rem"
        pb="1.5rem"
        borderBottom="1px solid"
        borderColor="var(--color-unify-white-30)"
        data-testid="admin-side-nav-function-buttons"
      >
        <Tooltip
          title={
            collapsed ? (
              <div style={{ fontSize: 14, padding: 4 }}>Exit Admin Mode</div>
            ) : null
          }
          arrow
          placement="right"
          sx={{ fontSize: 34 }}
        >
          <StyledButton
            collapsed={collapsed}
            variant="solid"
            onClick={handleExitAdminMode}
            data-testid="admin-side-nav-exit-btn"
          >
            <MdOutlineExitToApp />
            <span>Exit Admin Mode</span>
          </StyledButton>
        </Tooltip>
      </Box>

      {/* NAV SECTIONS */}
      {tabs.map((tab) => (
        <div
          key={tab.name}
          data-testid={`admin-side-nav-tab-${tab.name
            .replace(/\s+/g, '-')
            .toLowerCase()}`}
        >
          {/* Section Header */}

          <StyledSectionHeader
            collapsed={collapsed}
            data-testid={`admin-side-nav-header-${tab.name
              .replace(/\s+/g, '-')
              .toLowerCase()}`}
          >
            <span>{tab.name}</span>
          </StyledSectionHeader>
          {/* Render submenus */}
          {tab.submenus && (
            <div
              data-testid={`admin-side-nav-submenus-${tab.name
                .replace(/\s+/g, '-')
                .toLowerCase()}`}
            >
              {tab.submenus.map((submenu) => (
                <Tooltip
                  key={submenu.name}
                  title={
                    collapsed ? (
                      <div style={{ fontSize: 14, padding: 4 }}>
                        {submenu.name}
                      </div>
                    ) : null
                  }
                  arrow
                  placement="right"
                  sx={{ fontSize: 34 }}
                >
                  <StyledSubNavLink
                    isActive={isPathActive(submenu.path)}
                    onClick={() => handleNavClick(submenu.path)}
                    collapsed={collapsed}
                    data-testid={`admin-side-nav-sublink-${submenu.name
                      .replace(/\s+/g, '-')
                      .toLowerCase()}`}
                  >
                    {submenu.icon}
                    <span>{submenu.name}</span>
                  </StyledSubNavLink>
                </Tooltip>
              ))}
            </div>
          )}
        </div>
      ))}
    </StyledNavContainer>
  );
}
